import { supabase } from './supabase';

export type DinnerRecommendationStatus = 'pending' | 'accepted' | 'rejected';

export type DinnerRecommendation = {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  restaurant_id: string | null;
  bar_id: string | null;
  date: string | null;
  time: string | null;
  status: DinnerRecommendationStatus;
  created_at: string;
  updated_at: string;
  sender?: {
    id: string;
    full_name: string | null;
    profile_picture: string | null;
  };
  receiver?: {
    id: string;
    full_name: string | null;
    profile_picture: string | null;
  };
  restaurant?: any;
  bar?: any;
};

export const sendDinnerRecommendation = async (
  receiverId: string,
  message: string,
  restaurantId?: string,
  barId?: string,
  date?: string,
  time?: string
) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to send dinner recommendations');

    // Create the dinner recommendation
    const { data: newRecommendation, error: insertError } = await supabase
      .from('dinner_recommendations')
      .insert({
        sender_id: user.id,
        receiver_id: receiverId,
        message,
        restaurant_id: restaurantId || null,
        bar_id: barId || null,
        date: date || null,
        time: time || null,
        status: 'pending'
      })
      .select(`
        *,
        sender:profiles!dinner_recommendations_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!dinner_recommendations_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        restaurant:restaurants(*),
        bar:bars(*)
      `)
      .single();

    if (insertError) throw insertError;
    return newRecommendation;
  } catch (err) {
    console.error('Error sending dinner recommendation:', err);
    throw err;
  }
};

export const updateDinnerRecommendation = async (
  recommendationId: string,
  status: DinnerRecommendationStatus
) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to update dinner recommendations');

    // Update the recommendation status
    const { data: updatedRecommendation, error: updateError } = await supabase
      .from('dinner_recommendations')
      .update({ status })
      .eq('id', recommendationId)
      .select(`
        *,
        sender:profiles!dinner_recommendations_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!dinner_recommendations_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        restaurant:restaurants(*),
        bar:bars(*)
      `)
      .single();

    if (updateError) throw updateError;
    return updatedRecommendation;
  } catch (err) {
    console.error('Error updating dinner recommendation:', err);
    throw err;
  }
};

export const getPendingDinnerRecommendations = async () => {
  const maxRetries = 3;
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('You must be logged in to get dinner recommendations');

      // Get pending recommendations received by the user
      const { data: recommendations, error: recommendationsError } = await supabase
        .from('dinner_recommendations')
        .select(`
          *,
          sender:profiles!dinner_recommendations_sender_id_fkey(
            id, 
            full_name, 
            profile_picture
          ),
          receiver:profiles!dinner_recommendations_receiver_id_fkey(
            id, 
            full_name, 
            profile_picture
          ),
          restaurant:restaurants(*),
          bar:bars(*)
        `)
        .eq('receiver_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (recommendationsError) throw recommendationsError;
      return recommendations || [];
    } catch (err) {
      console.error(`Attempt ${attempt + 1} failed:`, err);
      attempt++;
      
      if (attempt < maxRetries) {
        // Wait before retrying with exponential backoff
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
      } else {
        // All retries failed, propagate the error
        throw err;
      }
    }
  }
  
  // This should never be reached due to the throw in the catch block
  throw new Error('Failed to get dinner recommendations after multiple retries');
};

export const getSentDinnerRecommendations = async () => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to get dinner recommendations');

    // Get recommendations sent by the user
    const { data: recommendations, error: recommendationsError } = await supabase
      .from('dinner_recommendations')
      .select(`
        *,
        sender:profiles!dinner_recommendations_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!dinner_recommendations_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        restaurant:restaurants(*),
        bar:bars(*)
      `)
      .eq('sender_id', user.id)
      .order('created_at', { ascending: false });

    if (recommendationsError) throw recommendationsError;
    return recommendations || [];
  } catch (err) {
    console.error('Error getting sent dinner recommendations:', err);
    throw err;
  }
};

export const getAcceptedDinnerRecommendations = async () => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to get dinner recommendations');

    // Get accepted recommendations (both sent and received)
    const { data: recommendations, error: recommendationsError } = await supabase
      .from('dinner_recommendations')
      .select(`
        *,
        sender:profiles!dinner_recommendations_sender_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        receiver:profiles!dinner_recommendations_receiver_id_fkey(
          id, 
          full_name, 
          profile_picture
        ),
        restaurant:restaurants(*),
        bar:bars(*)
      `)
      .eq('status', 'accepted')
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (recommendationsError) throw recommendationsError;
    return recommendations || [];
  } catch (err) {
    console.error('Error getting accepted dinner recommendations:', err);
    throw err;
  }
};